import React, { useState, useCallback, useRef } from 'react';

import { Button } from '@/components/atoms/NewPcButton';

import PlusIcon from '@/assets/icons/Monitoring/PlusIcon.svg?react';
import CheckIcon from '@/assets/icons/Monitoring/CheckIcon.svg?react';
import { ImageIcon, Loader2 } from 'lucide-react';

import { useEffect } from 'react';

import { Dialog, DialogContent, DialogClose } from '@/components/atoms/Dialog';
import { usePost, CPLAT_API_ROUTES } from '@/api';
import { useToast } from '@/hooks/V3/useToast';
import { useAppSelector } from '@/redux/store';
import { cplatApiV2 } from '@/api';
import useCheckMembership from '@/hooks/useCheckMembership';
import DeliveryIcon from '@/assets/icons/Monitoring/IconTagDelivery.svg?react';
import KeywordTable from './KeywordTable';
import { useMonitoringGoods } from './MonitoringContext';
import { MonitoringGoodsDetail } from '@/types/api';

const RegisteredMonitoringEdit = ({
  goods,
  dialogOpen,
  setDialogOpen,
  isMockGoods = false,
}: {
  goods: MonitoringGoodsDetail;
  dialogOpen: boolean;
  setDialogOpen: (open: boolean) => void;
  isMockGoods?: boolean; // 모킹된 데이터 여부
}) => {
  const { showToast } = useToast();
  const user = useAppSelector((state) => state.user);
  const { refetch } = useMonitoringGoods();
  const [savedKeywords, setSavedKeywords] = useState<string[]>([]);
  const [isLoadingSaved, setIsLoadingSaved] = useState(false);
  const [relatedList, setRelatedList] = useState<string[]>([]);
  const [input, setInput] = useState('');

  const isMember = useCheckMembership();

  // 키워드 변경 mutation (API 엔드포인트/파라미터는 실제 명세에 맞게)
  const { mutate: updateKeywords, isPending: isUpdating } = usePost(
    CPLAT_API_ROUTES.setUserMonitoringGoods,
  );

  const [isChanged, setIsChanged] = useState(false);
  const keywordTableRef = useRef<HTMLDivElement>(null);

  const handleChageKeywords = () => {
    if (isMockGoods) {
      showToast('warning', '예시 상품은 키워드 변경을 할 수 없어요');
      return;
    }
    updateKeywords(
      {
        cplatToken: user.CplatToken,
        StoreName: goods.StoreName,
        GoodsNo: goods.GoodsNo,
        GoodsImageUrl: goods.GoodsImageUrl,
        GoodsName: goods.GoodsName,
        CategoryFullName: goods.CategoryFullName,
        UseYn: 'Y',
        RelatedKeywordList: relatedList,
        GoodsUrl: goods.GoodsUrl,
        GoodsPrice: goods.GoodsPrice,
        relatedKeywordRankInfo: {}, // 셀러박스에서는 변화량 데이터 빈 객체로 전달, 1일 이후 자동 수집
        ShippingFee: goods.ShippingFee,
        GoodsRegisterDate: goods.GoodsRegisterDate,
        NoticeYn: goods.NoticeYn,
        IsMyStoreGoods: goods.IsMyStoreGoods,
        MallSeq: goods.MallSeq,
        InsertYn: 'N', // 기존 등록된 상품이므로 N
      },
      {
        onSuccess: () => {
          showToast(
            'success',
            '추적키워드가 변경 되었어요. 순위 추적까지 최대 하루가 소요될 수 있어요.',
          );
          refetch();
          setDialogOpen(false);
        },
        onError: () => {
          showToast('warning', '키워드 변경에 실패했습니다. 다시 시도해주세요.');
        },
      },
    );
  };

  useEffect(() => {
    if (!dialogOpen) return;
    const fetchSavedKeywords = async () => {
      setIsLoadingSaved(true);
      try {
        const res = await cplatApiV2.post(
          CPLAT_API_ROUTES.getUserMonitoringGoodsRecommendedKeywords,
          {
            cplatToken: user.CplatToken,
            goodsNo: String(goods.GoodsNo),
          },
        );
        const data = res.data?.data;
        const result = Array.isArray(data) && data.length > 0 ? data[0] : null;
        setSavedKeywords(result?.RecommendedKeywordList || result?.recommendedKeywordList || []);
      } catch {
        setSavedKeywords([]);
      } finally {
        setIsLoadingSaved(false);
      }
    };
    fetchSavedKeywords();
    setRelatedList(goods.RelatedKeyword || []);
    setInput('');
    setIsChanged(false);
  }, [dialogOpen, goods.GoodsNo, goods.RelatedKeyword, user.CplatToken]);

  const MAX_KEYWORDS = isMockGoods ? 10 : isMember ? 10 : 2;

  // 키워드 토글
  const handleToggleKeyword = (keyword: string) => {
    if (isMockGoods) {
      showToast('warning', '예시 상품은 키워드 선택을 할 수 없어요');
      return;
    }
    setRelatedList((prev) => {
      if (prev.includes(keyword)) {
        setIsChanged(true);
        return prev.filter((k) => k !== keyword);
      } else if (prev.length < MAX_KEYWORDS) {
        setIsChanged(true);
        return [...prev, keyword];
      } else {
        showToast('warning', `최대 ${MAX_KEYWORDS}개까지 선택할 수 있습니다.`);
        return prev;
      }
    });
  };

  // 키워드 직접 입력 추가
  const handleAddKeyword = () => {
    if (isMockGoods) {
      showToast('warning', '예시 상품은 키워드 추가를 할 수 없어요');
      return;
    }
    const trimmed = input.trim();
    if (!trimmed) return;

    setRelatedList((prev) => {
      if (prev.includes(trimmed)) {
        showToast('warning', '이미 추가된 키워드예요');
        return prev;
      }
      if (prev.length >= MAX_KEYWORDS) {
        showToast('warning', `최대 ${MAX_KEYWORDS}개까지 선택할 수 있습니다.`);
        return prev;
      }
      setIsChanged(true);
      setInput('');
      setTimeout(() => {
        keywordTableRef.current?.scrollTo({ top: keywordTableRef.current.scrollHeight, behavior: 'smooth' });
      }, 50);
      return [...prev, trimmed];
    });
  };

  // 키워드 삭제
  const handleRemoveKeyword = (keyword: string) => {
    if (isMockGoods) {
      showToast('warning', '예시 상품은 키워드 삭제를 할 수 없어요');
      return;
    }
    setRelatedList((prev) => prev.filter((k) => k !== keyword));
    setIsChanged(true);
  };

  return (
    <DialogContent noCloseBtn className="max-w-[1020px]">
      <div className="relative flex flex-col gap-[10px]">
        {/* 2. 상품 정보 */}
        <div className="flex  gap-3">
          <EditKeywordImage src={goods.GoodsImageUrl} alt={goods.GoodsName} />
          <div className="flex flex-col gap-[10px]">
            <div className="flex gap-[20px]">
              <div className="flex flex-1 flex-col gap-[10px]">
                <div className="flex flex-col gap-[6px]">
                  <div className="text-gr-800 Body1S16">{goods.GoodsName}</div>
                  <div className="flex flex-col gap-0.5 text-gr-600 Caption1M12">
                    <div className="">{goods.CategoryFullName}</div>
                    <div>제품등록일 : {formatDate(goods.GoodsRegisterDate)}</div>
                  </div>
                </div>
                <div className="flex min-w-[100px] items-center gap-3">
                  <div className="text-gr-800 Body1S16">{goods.GoodsPrice?.toLocaleString()}원</div>
                  <div className="flex items-center gap-[8px]">
                    <div className="flex h-[20px] w-[20px] rounded-full bg-[#F3F3F4] p-[3px]">
                      <DeliveryIcon />
                    </div>
                    <div className="text-gr-800 Body5M14">
                      {goods.ShippingFee?.toLocaleString()}원
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 3. 키워드 선택 */}
        <div className="flex h-[350px] gap-[20px]">
          <div className="flex w-[360px] flex-col gap-[20px] rounded-[16px] border border-gr-200 p-4">
            <div className="text-gr-800 Body3S15">순위를 추적할 키워드를 입력하거나 선택하세요</div>
            <div className="flex h-[54px] items-center rounded-[16px] border border-gr-300 bg-white px-[12px] py-[10px]  focus-within:border-point-400">
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="키워드 입력"
                className="h-fit flex-1 pl-[4px] pr-[10px] text-gr-800 Body5M14 focus:outline-none"
                onKeyDown={(e) => e.key === 'Enter' && !e.nativeEvent.isComposing && handleAddKeyword()}
                readOnly={relatedList.length >= MAX_KEYWORDS || isMockGoods}
                tabIndex={relatedList.length >= MAX_KEYWORDS || isMockGoods ? -1 : undefined}
                onClick={() => {
                  if (relatedList.length >= MAX_KEYWORDS) {
                    showToast('warning', `${MAX_KEYWORDS}개까지 입력가능합니다`);
                  }
                }}
              />
              <Button
                variant="secondary"
                className="h-fit gap-[6px] px-[10px] py-[6px] Body5M14"
                onClick={handleAddKeyword}
                disabled={relatedList.length >= MAX_KEYWORDS || !input.trim()}
              >
                추가
              </Button>
            </div>

            <div className="scrollbar relative flex flex-1 flex-wrap content-start gap-2 overflow-auto">
              {/* 추천 키워드 버튼들 */}
              {savedKeywords.map((keyword) => (
                <button
                  key={keyword}
                  type="button"
                  onClick={() => handleToggleKeyword(keyword)}
                  className={`Body5M14 flex items-center rounded-[19px] border px-[10px] py-1 text-sm text-gr-700 transition-colors
        ${relatedList.includes(keyword) ? ' border-green-400 ' : 'border-gr-200 '}
      `}
                >
                  {relatedList.includes(keyword) ? (
                    <CheckIcon className="mr-1 h-4 w-4 text-point-500" />
                  ) : (
                    <PlusIcon className="mr-1 h-4 w-4 text-gr-400" />
                  )}
                  {keyword}
                </button>
              ))}

              {/* 로딩 표시 */}
              {isLoadingSaved && (
                savedKeywords.length > 0 ? (
                  <Loader2 className="h-4 w-4 animate-spin self-center text-gr-400" />
                ) : (
                  <div className="absolute inset-0 z-10 flex items-center justify-center rounded-[16px] bg-white/70">
                    <span className="text-gr-500 Body5M14">추천 키워드를 불러오는 중...</span>
                  </div>
                )
              )}
              {/* 빈 결과 메시지 */}
              {!isLoadingSaved && savedKeywords.length === 0 && (
                <div className="absolute inset-0 z-10 flex items-center justify-center rounded-[16px] bg-white/70">
                  <span className="text-gr-500 Body5M14">추천할 키워드를 찾지 못했어요</span>
                </div>
              )}
            </div>
          </div>
          <div className="flex flex-1 flex-col rounded-[16px] border border-gr-200 p-4">
            <div className=" flex gap-[10px] ">
              <div className="text-gr-800 Body3S15">추가한 키워드예요</div>
              <div className="Body4B14">
                <span className=" text-point-500">{relatedList.length}</span>
                <span className="text-gr-500"> / {MAX_KEYWORDS}</span>
              </div>
            </div>
            <KeywordTable
              ref={keywordTableRef}
              goods={goods}
              isMockGoods={isMockGoods}
              relatedList={relatedList}
              onRemove={handleRemoveKeyword}
            />
          </div>
        </div>
        {/* 5. 하단 버튼 */}
        <div className=" flex justify-center gap-[12px]">
          <Button
            variant="secondary"
            className="max-w-[300px] flex-1 Body5M14"
            onClick={() => {
              setDialogOpen(false);

              setRelatedList([]);
            }}
          >
            닫기
          </Button>
          <Button
            className="max-w-[300px] flex-1 Body5M14"
            onClick={handleChageKeywords}
            disabled={isUpdating || !isChanged}
            isLoading={isUpdating}
          >
            키워드 변경하기
          </Button>
        </div>
      </div>
    </DialogContent>
  );
};

export default RegisteredMonitoringEdit;

const EditKeywordImage = ({ src, alt }: { src?: string; alt?: string }) => {
  const [hasError, setHasError] = useState(!src);
  const handleError = useCallback(() => setHasError(true), []);

  if (hasError) {
    return (
      <div className="flex h-[120px] w-[120px] shrink-0 items-center justify-center rounded-[8px] border border-gr-200 bg-gr-50">
        <ImageIcon className="h-10 w-10 text-gr-300" />
      </div>
    );
  }

  return (
    <img
      src={src}
      alt={alt}
      onError={handleError}
      className="h-[120px] w-[120px] shrink-0 rounded-[8px] border object-cover"
    />
  );
};

function formatDate(dateStr: any) {
  if (!dateStr) return '-';
  // "2025-06-16T15:38:18" → "2025.06.16"
  return dateStr.slice(0, 10).replace(/-/g, '.');
}
